import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { searchRecordRaw } from '@/store/actions/searchAction'
import { canUseControlWorkspace, readWorkspace, WORKSPACES } from '@/hooks/useWorkspace'

/**
 * W68 (UI-006) · Search across the record, from the header.
 *
 * "Search must cover documents, clauses, obligations, rules, portfolios,
 * instruments, issuers, counterparties, results, cases, waivers,
 * attestations, comments, and reports, subject to permissions." There was
 * no search box in the Control workspace at all. This one asks GET /search
 * as the reviewer types, shows the hits grouped by kind with the deep link
 * each carries, and — because "subject to permissions" is a promise about
 * what was NOT searched as much as what was — says which kinds were left
 * out and why.
 */

const KIND_LABEL = {
  documents: 'Documents', clauses: 'Clauses', obligations: 'Obligations', rules: 'Rules',
  portfolios: 'Portfolios', instruments: 'Instruments', issuers: 'Issuers', counterparties: 'Counterparties',
  results: 'Results', cases: 'Cases', waivers: 'Waivers', attestations: 'Attestations',
  comments: 'Comments', reports: 'Reports'
}

const GlobalSearch = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const orgRoles = useSelector((state) => state.organisationContext?.roles || [])
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [outcome, setOutcome] = useState(null)
  const [busy, setBusy] = useState(false)
  const timer = useRef(null)
  const box = useRef(null)

  const inControl = readWorkspace() === WORKSPACES.CONTROL || location.pathname.startsWith('/compliance')
  const allowed = canUseControlWorkspace(orgRoles)

  const run = useCallback((text) => {
    if (text.trim().length < 2) { setOutcome(null); return }
    setBusy(true)
    dispatch(searchRecordRaw({ params: { q: text.trim() } }))
      .then((res) => setOutcome(res.data?.data || null))
      .catch(() => setOutcome(null))
      .finally(() => setBusy(false))
  }, [dispatch])

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => run(q), 250)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [q, run])

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setOpen(true) }
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    const onClick = (event) => { if (box.current && !box.current.contains(event.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  useEffect(() => { setOpen(false) }, [location.pathname, location.search])

  if (!allowed || !inControl) return null

  const grouped = (outcome?.hits || []).reduce((acc, hit) => {
    if (!acc[hit.kind]) acc[hit.kind] = []
    acc[hit.kind].push(hit)
    return acc
  }, {})

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 rounded-md border border-borderV1 bg-white px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900"
        aria-label="Search the record"
        title="Search documents, clauses, rules, portfolios, results, cases and more (Ctrl+K)"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Search</span>
        <kbd className="hidden md:inline text-[10px] text-gray-400 border border-borderV1 rounded px-1">Ctrl K</kbd>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-[min(92vw,40rem)] rounded-lg border border-borderV1 bg-white shadow-lg z-[60]">
          <div className="flex items-center gap-2 border-b border-borderV1 px-3 py-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              autoFocus
              className="flex-1 text-sm outline-none bg-transparent"
              placeholder="Search documents, clauses, obligations, rules, portfolios, instruments, issuers, counterparties, results, cases, waivers, attestations, comments, reports"
              value={q}
              onChange={(event) => setQ(event.target.value)}
              aria-label="Search query"
            />
            {q && <button type="button" onClick={() => setQ('')} aria-label="Clear"><X className="w-4 h-4 text-gray-400" /></button>}
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-2">
            {busy && <p className="px-2 py-1 text-xs text-gray-500">Searching…</p>}
            {!busy && q.trim().length < 2 && <p className="px-2 py-2 text-xs text-gray-500">Type at least two characters. Every kind you may view is searched; the ones you may not are listed, not hidden.</p>}
            {!busy && outcome && outcome.hits.length === 0 && q.trim().length >= 2 && (
              <p className="px-2 py-2 text-xs text-gray-500">Nothing matches “{outcome.q}” in {outcome.searched?.length || 0} kind(s).</p>
            )}
            {Object.entries(grouped).map(([kind, hits]) => (
              <div key={kind} className="mb-2">
                <div className="px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{KIND_LABEL[kind] || kind} <span className="text-gray-400 font-normal">{outcome.counts?.[kind] === outcome.perKindLimit ? `first ${outcome.perKindLimit}` : hits.length}</span></div>
                <ul>
                  {hits.map((hit) => (
                    <li key={`${hit.kind}-${hit.id}`}>
                      <button
                        type="button"
                        className="w-full text-left rounded px-2 py-1.5 hover:bg-backgroundV1 focus:bg-backgroundV1 outline-none"
                        onClick={() => { setOpen(false); navigate(hit.href) }}
                      >
                        <div className="text-sm text-slate-800 truncate">{hit.title}</div>
                        {hit.subtitle && <div className="text-[11px] text-gray-500 truncate">{hit.subtitle}</div>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {outcome?.omitted?.length > 0 && (
              <p className="px-2 pt-1 text-[11px] text-amber-800 border-t border-borderV1">
                Not searched: {outcome.omitted.map((entry) => KIND_LABEL[entry.kind] || entry.kind).join(', ')} — your roles do not carry the capability that governs them.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default GlobalSearch
